import { defineStore } from "pinia";
import { ref } from "vue";

export interface QuizSubmissionPayload {
    currentQuestion?: string | null;
    score?: number | null;
    totalQuestions?: number | null;
    answersJson?: string | null;
    completed?: boolean;
    userAgent?: string | null;
    path?: string | null;
}

export const useFormieStore = defineStore("formie", () => {
    const endpoint = ref<string>("/api");
    const token = ref<string>("");
    const uuid = ref<string>(crypto.randomUUID());
    const mutationName = "save_carbonQuiz_Submission";
    const typeName = "carbonQuizSubmission";

    const submissionId = ref<number | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);

    function setConfig(opts: {
        endpoint?: string;
        token?: string;
        uuid?: string;
    }) {
        if (opts.endpoint) endpoint.value = opts.endpoint;
        if (opts.token) token.value = opts.token;
        if (opts.uuid) uuid.value = opts.uuid;
    }

    const mutation = `
        mutation SubmitQuiz(
          $id: ID
          $uuid: String
          $currentQuestion: String
          $score: Number
          $totalQuestions: Number
          $answersJson: String
          $completed: String
          $userAgent: String
          $path: String
        ) {
          save_carbonQuiz_Submission(
            id: $id
            uuid: $uuid
            currentQuestion: $currentQuestion
            score: $score
            totalQuestions: $totalQuestions
            answersJson: $answersJson
            completed: $completed
            userAgent: $userAgent
            path: $path
          ) {
            id
            uuid
            ... on carbonQuiz_Submission {
              uuid
            }
          }
        }
      `;

    async function createOrUpdateSubmission(payload: QuizSubmissionPayload) {
        if (!endpoint.value) return;

        loading.value = true;
        error.value = null;

        const ua = payload.userAgent ?? navigator.userAgent;
        const path =
            payload.path ?? window.location.pathname + window.location.hash;

        const variables = {
            id: submissionId.value ?? null,
            uuid: uuid.value,
            currentQuestion: payload.currentQuestion ?? null,
            score: payload.score ?? null,
            totalQuestions: payload.totalQuestions ?? null,
            answersJson: payload.answersJson ?? null,
            completed: payload.completed ? "1" : "0",
            userAgent: ua,
            path,
        };

        // Logging
        // console.groupCollapsed("📝 Formie submission payload");
        // console.log({
        //     endpoint: endpoint.value,
        //     uuid: uuid.value,
        //     submission: variables,
        // });
        // console.groupEnd();

        if (!token.value) return;

        try {
            const res = await fetch(endpoint.value, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token.value
                        ? { Authorization: `Bearer ${token.value}` }
                        : {}),
                },
                body: JSON.stringify({
                    query: mutation,
                    variables,
                }),
            });

            const json = await res.json();

            if (json.errors) {
                console.error(json.errors);
                error.value = json.errors[0]?.message ?? "Unknown error";
                return;
            }

            const data = json.data?.[mutationName];
            if (!data) {
                error.value = "No submission returned.";
                return;
            }

            const returnedId = data.id;
            submissionId.value = returnedId;
        } catch (err: any) {
            console.error(err);
            error.value = err.message;
        } finally {
            loading.value = false;
        }
    }
    return {
        endpoint,
        token,
        uuid,
        submissionId,
        loading,
        error,
        setConfig,
        createOrUpdateSubmission,
    };
});
